/**
 * Admin App — Centralized Axios API client with interceptors.
 * Injects Bearer token, handles 401 redirects, standardizes errors.
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import config from './config';
import logger from './logger';

const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Request interceptor: attach JWT token ──
apiClient.interceptors.request.use(
  (reqConfig: InternalAxiosRequestConfig) => {
    const token = Cookies.get(config.tokenCookieName);
    if (token && reqConfig.headers) {
      reqConfig.headers.Authorization = `Bearer ${token}`;
    }
    return reqConfig;
  },
  (error) => {
    logger.error('request interceptor error', { error: error?.message });
    return Promise.reject(error);
  }
);

// ── Response interceptor: handle 401, normalize errors ──
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string; success?: boolean }>) => {
    const status = error.response?.status;
    const url = error.config?.url || 'unknown';

    if (status === 401) {
      logger.warn('authentication expired, redirecting to signin', { url });
      // Token expired or invalid — clear and redirect to signin
      Cookies.remove(config.tokenCookieName);
      // Preserve where the user was so signin can send them back, and avoid
      // re-entering the redirect while already on the signin screen.
      if (window.location.pathname !== '/signin') {
        const from = window.location.pathname + window.location.search;
        window.location.replace(`/signin?from=${encodeURIComponent(from)}`);
      }
    } else if (status && status >= 500) {
      logger.error('server error', { status, url, message: error.response?.data?.message });
    } else if (status && status >= 400) {
      logger.warn('client error', { status, url, message: error.response?.data?.message });
    } else {
      logger.error('network error', { url, message: error.message });
    }

    // Normalize error message
    const message =
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';

    return Promise.reject(new Error(message));
  }
);

export default apiClient;

/**
 * Helper to build service-specific URL paths.
 * Usage: apiClient.get(serviceUrl('ums', '/api/v1/customers'))
 */
export function serviceUrl(
  service: keyof typeof config.services,
  path: string
): string {
  return `${config.services[service]}${path}`;
}
